// mapIntentsBus — Queue für "bitte Map, mach was ich sage"-Kommandos, die
// aus Nicht-Map-Seiten (z. B. TrailsPage) kommen.
//
// Warum ein Bus statt ein Ref / Context?
//   Die MapPage ist dauerhaft im DOM gemountet (siehe App.tsx), aber wenn
//   der User auf /trails ist, existiert dort gar keine Verbindung zum
//   MapPage-State. Navigieren wir einfach programmatisch zu '/', mounten
//   wir MapPage NICHT neu (sie war die ganze Zeit da) — der Bus muss also
//   das Intent puffern und MapPage zieht es beim nächsten Render-Cycle ab.
//
// Semantik:
//   - Ein Intent beinhaltet eine trailId und ein Signal "selectAndFit".
//   - publish() schreibt das Intent in den Bus und feuert einen Listener.
//   - consume() wird von MapPage nach dem State-Update aufgerufen und leert
//     das Intent (sonst würde es beim nächsten Render-Cycle nochmal greifen).
//
// Warum nicht NavigateLink mit State? React-Router hat location.state, aber:
//   1) Refresh / Tab-Zurück verliert's.
//   2) MapPage ist IMMER gemountet — ein Intent-Pattern ist sauberer als
//      in location.state zu rumoren.
//   3) Mehrfach-Feuer (Mehrfach-Tap im Listing) soll idempotent sein: das
//      neueste Intent "gewinnt", alte werden einfach überschrieben.

import type { CachedTrail } from '../trails';

type Listener = (intent: MapIntent | null) => void;

export interface MapIntent {
  /**
   * ULTRA: eine flüchtige, NICHT gespeicherte Route (z.B. „RIDE" aus dem
   * Pit-Stop-Voraus-Flow). MapPage selektiert sie direkt (kein IDB-Load) und
   * startet bei autoStartRide sofort den Ride-Modus. Siehe ephemeralRouteAsTrail.
   */
  ephemeralTrail?: CachedTrail;
  /**
   * Der Trail, der selektiert + gefittet werden soll. Genau EINS von
   * `trailId`, `runId` oder `customTrailId` muss gesetzt sein.
   */
  trailId?: string;
  /**
   * Wave 14ag: ein DH-Run soll als Pseudo-Trail auf der Map angezeigt werden.
   * Die MapPage bastelt sich aus dem Run + Trail-Cache eine CachedTrail-Hülle
   * (siehe lib/runAsTrail), selektiert sie wie ein normaler Trail, und kann
   * damit alle bestehenden Mechanismen (Aura, ElevationProfile, RIDE-Button,
   * Navigate-to-start) weiterbenutzen.
   */
  runId?: string;
  /**
   * Wave 14bv Stage 6m: ein device-lokaler Custom-Trail soll auf der Map
   * geöffnet werden. Wird per lib/customTrailAsTrail in eine CachedTrail
   * gewrappt. Dadurch verhält sich die Custom-Trail-Selection ab diesem
   * Moment identisch zu einer normalen Trail-Selection (RIDE-Button,
   * Navigate-to-start, Elevation-Profile per SRTM auf-the-fly).
   */
  customTrailId?: string;
  /**
   * Wave 14ca-fix48: eine admin-kuratierte Featured Tour soll auf der Map
   * geöffnet werden. MapPage parsed das GPX onthe-fly (analog Run) und
   * wrapped es in eine CachedTrail-Hülle (siehe featuredTourAsTrail).
   */
  featuredTourId?: string;
  /** Wenn gesetzt, setzt die MapPage zusätzlich den Mode auf 'explore'
   *  (damit kein Ride-Mode-Residual-State die Anzeige verfälscht). */
  resetMode?: boolean;
  /** Wave 14ch: "Save & ride now"-Flow auf CreateCustomPage. Sobald der
   *  Intent appliziert ist (Custom-Trail/Featured-Tour/Run geladen +
   *  selektiert), startet die MapPage automatisch den Ride-Modus. Das
   *  durchläuft den normalen startRide-Pfad, inkl. RideIntroModal-Gate
   *  beim ersten Mal. */
  autoStartRide?: boolean;
}

class MapIntentsBus {
  private current: MapIntent | null = null;
  private readonly listeners = new Set<Listener>();

  /** Liefert das aktuelle Intent (oder null wenn keins anliegt). */
  peek(): MapIntent | null {
    return this.current;
  }

  /** Liest + löscht das Intent (atomic). Nur der Consumer (MapPage) ruft das auf. */
  consume(): MapIntent | null {
    const intent = this.current;
    this.current = null;
    return intent;
  }

  /** Setzt ein neues Intent. Ein evtl. wartendes wird überschrieben. */
  publish(intent: MapIntent): void {
    this.current = intent;
    for (const l of this.listeners) l(intent);
  }

  /** Subscriber fürs "neues Intent da"-Signal. Wird beim Mount von MapPage
   *  angehängt, damit sie sofort reagieren kann, falls sie grad gemountet
   *  war als das Intent gepostet wurde. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

export const mapIntentsBus = new MapIntentsBus();
